import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { loginParams } from "services/types";
import authServices from "services/auth.services";
import { AuthState } from "features/types";
import { Platform } from "react-native";
import {
  authKey,
  deleteSecureStoredToken,
  getSecureStoredToken,
  getStoredAuth,
  saveSecureToken,
  setStoredAuth,
} from "utils/storageUtils";

const initialState: AuthState = {
  username: undefined,
  id: undefined,
  loading: false,
};

export const login = createAsyncThunk(
  "auth/login",
  async (loginParams: loginParams, { dispatch }) => {
    const response = await authServices.login(loginParams);
    dispatch(loginSucceed(response));
  }
);

export const tryLocalSignIn = createAsyncThunk(
  "auth/tryLocalSignIn",
  async (_, { dispatch }) => {
    try {
      const storedAuth = await getStoredAuth();
      // console.log(storedAuth);

      if (storedAuth !== null) {
        const token = Platform.OS === "web" ? "" : await getSecureStoredToken();
        dispatch(
          loginSucceed({
            username: storedAuth.username,
            id: Number(storedAuth.id),
            token,
          })
        );
      }
    } catch (e) {
      console.error(JSON.stringify(e));
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginSucceed: (
      state,
      action: PayloadAction<{
        username: string;
        id: number;
        token: string | null;
      }>
    ) => {
      const { username, id, token } = action.payload;
      setStoredAuth({ username, id });
      state.username = username;
      state.id = id;

      // For native app, we want to store the JWT token in secure store and grab it when sending requests
      Platform.OS !== "web" && token !== null && saveSecureToken(token);
    },
    logout: (state) => {
      AsyncStorage.removeItem(authKey);
      state.id = undefined;
      state.loading = false;
      state.username = undefined;
      Platform.OS !== "web" && deleteSecureStoredToken();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
      })
      .addCase(login.rejected, (state) => {
        state.loading = false;
      })
      .addCase(login.fulfilled, (state) => {
        state.loading = false;
      });
  },
});

export const { logout, loginSucceed } = authSlice.actions;

export default authSlice.reducer;
